import React, { useState, useEffect, useRef } from 'react';
import { Table, Tag, Button, Row, Col } from 'antd';
import { 
  CheckCircleOutlined, 
  SyncOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined 
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { format } from 'rut.js';
import { getBlocks } from '../../../../services/general';
import { createReservation, reservationToConfirm } from '../../../../services/reservation';
import evaluationTypes from '../../../../utils/evaluationType.json';
import ExcelJS from 'exceljs';
import config from '../../../../config';
const MassiveChargeStep4 = ({ form, handleCancel, finalDataSource, baseData, overCapacityToken, fieldsToInclude }) => {
  const { t } = useTranslation();
  const [processList, setProcessList] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const initialized = useRef(false);
  useEffect(() => {
    if (!finalDataSource || finalDataSource.length === 0) return;
    if (initialized.current) return;
    initialized.current = true;
    const runTasks = async () => {
      setIsProcessing(true);
      const formValues = baseData || {};
      const branchOfficeId = formValues.branch_office_id;
      const grouped = finalDataSource.reduce((acc, current) => {
        const applicantObj = current.applicant || {};
        const applicantId = typeof applicantObj === 'object' ? applicantObj.id : applicantObj;
        const applicantName = typeof applicantObj === 'object' 
          ? (applicantObj.email || applicantObj.fullname || applicantObj.name) 
          : t('reservations.massiveCharge.step4UnknownApplicant');
        const costCenterObj = current.costCenter;
        const costCenterId = typeof costCenterObj === 'object' && costCenterObj !== null && costCenterObj.id ? costCenterObj.id : (formValues.cost_center_id || 'null');
        const groupKey = `${current.reservationDate}_${applicantId}_${costCenterId}`;
        if (!acc[groupKey]) {
          acc[groupKey] = {
            key: groupKey,
            date: current.reservationDate,
            applicantName: applicantName,
            applicantId: applicantId,
            totalPatients: 0,
            dataSourceSubset: [],
            status: 'pending',
            errorReason: '',
            reservationId: null
          };
        }
        acc[groupKey].dataSourceSubset.push(current);
        acc[groupKey].totalPatients += 1;
        return acc;
      }, {});
      const taskArray = Object.values(grouped);
      setProcessList([...taskArray]);
      for (let i = 0; i < taskArray.length; i++) {
        const item = taskArray[i];
        setProcessList(prev => {
          const next = [...prev];
          next[i] = { ...next[i], status: 'processing', errorReason: t('reservations.massiveCharge.step4ProcessingGetBlocks') };
          return next;
        });
        try {
          const mDate = moment(item.date, ['DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601]).format('YYYY-MM-DD');
          let blocksResponse;
          try {
            blocksResponse = await getBlocks({
              branchOffice: branchOfficeId,
              date: mDate,
              department: 1 
            });
          } catch(e) {
            throw new Error(t('reservations.massiveCharge.step4ErrorBlocks'));
          }
          const rawBlocks = blocksResponse?.blocks || blocksResponse?.data || [];
          const availableBlocks = rawBlocks.filter(b => {
            const occupied = b.occupied || b.reservations_count || b.count || 0;
            return (b.max_capacity - occupied) > 0;
          });
          const totalAvailable = availableBlocks.reduce((acc, b) => acc + (b.max_capacity - (b.occupied || b.reservations_count || b.count || 0)), 0);
          const formatPatientToPayload = (p, b, extraPayload = {}) => {
            let formattedPhone = p.phone;
            if (formattedPhone && formattedPhone.length > 4) {
              if (!formattedPhone.startsWith('+')) {
                formattedPhone = `+${formattedPhone}`;
              }
            } else {
              formattedPhone = null;
            }
            let formattedDoc = p.documentNumber;
            if (String(p.documentType).toLowerCase() === 'rut' && formattedDoc) {
              formattedDoc = format(formattedDoc, { dots: false }); 
            }
            const normalizeDocumentType = (type) => {
              if (!type) return null;
              const lowerType = String(type).toLowerCase().trim();
              if (lowerType === 'dni') return 'dni';
              if (lowerType === 'passport' || lowerType === 'pasaporte') return 'passport';
              if (lowerType === 'ce' || lowerType === 'carnet de extranjería' || lowerType === 'carnet de extranjeria') return 'ce';
              return lowerType;
            };
            let formattedBirthDate = null;
            if (p.birthDate) {
              const momentBirthDate = moment(p.birthDate, ['DD-MM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
              if (momentBirthDate.isValid()) {
                formattedBirthDate = momentBirthDate.format('YYYY-MM-DD');
              }
            }
            const peEvals = evaluationTypes.PE || [];
            const evalTypeObj = peEvals.find(e => e.text === p.evaluationType);
            return {
              block_id: b.id,
              block: b.block,
              document_type: normalizeDocumentType(p.documentType),
              document_number: formattedDoc || null,
              full_name: `${p.names} ${p.lastnames}`,
              names: p.names || null,
              last_name: p.lastnames || null,
              evaluation_type: evalTypeObj ? evalTypeObj.value : null,
              phone: formattedPhone || null,
              email: p.email || null,
              external_id: p.externalId || null,
              profession: typeof p.jobPosition === 'object' && p.jobPosition !== null ? p.jobPosition.name : (p.jobPosition || null),
              job_position_id: typeof p.jobPosition === 'object' && p.jobPosition !== null ? p.jobPosition.id : null,
              area_id: typeof p.area === 'object' && p.area !== null ? p.area.id : null,
              project_id: typeof p.project === 'object' && p.project !== null ? p.project.id : null,
              gender: p.gender === 'Masculino' ? '0' : p.gender === 'Femenino' ? '1' : null,
              birthdate: formattedBirthDate || null,
              prestations: (p.protocol || []).map(proto => {
                const cleaned = { ...proto };
                if (cleaned.price !== undefined && cleaned.sale_amount === undefined) {
                  cleaned.sale_amount = cleaned.price;
                }
                delete cleaned.price;
                delete cleaned._originalName;
                return cleaned;
              }),
              ...extraPayload
            };
          };
          let isCampaignBranch = false;
          if (config?.campaingBranches && baseData?.branch_office_id) {
            const allowedIds = config.campaingBranches.split(',').map(id => String(id).trim());
            isCampaignBranch = allowedIds.includes(String(baseData.branch_office_id));
          }
          const hasOvercapacityPrivilege = overCapacityToken || isCampaignBranch;
          if (totalAvailable < item.totalPatients) {
            if (!hasOvercapacityPrivilege) {
              throw new Error(t('reservations.massiveCharge.step4ErrorMissingCapacity', { disp: totalAvailable }));
            }
          }
          setProcessList(prev => {
            const next = [...prev];
            next[i] = { ...next[i], errorReason: t('reservations.massiveCharge.step4ProcessingAssigning') };
            return next;
          });
          let patientCursor = 0;
          let finalPatients = [];
          const patientsToAssign = item.dataSourceSubset;
          for (const b of availableBlocks) {
            const occupied = b.occupied || b.reservations_count || b.count || 0;
            let available = b.max_capacity - occupied;
            while (available > 0 && patientCursor < patientsToAssign.length) {
              const p = patientsToAssign[patientCursor];
              p._assignedBlock = b.block;
              finalPatients.push(formatPatientToPayload(p, b));
              available--;
              patientCursor++;
            }
            if (patientCursor >= patientsToAssign.length) break;
          }
          if (patientCursor < patientsToAssign.length && hasOvercapacityPrivilege) {
            const defaultBlock = rawBlocks[0];
            if (!defaultBlock) {
               throw new Error(t('reservations.massiveCharge.step4ErrorNoBlocks'));
            }
            while (patientCursor < patientsToAssign.length) {
              const p = patientsToAssign[patientCursor];
              p._assignedBlock = defaultBlock.block;
              finalPatients.push(formatPatientToPayload(p, defaultBlock, {
                over_capacity: true,
                code: overCapacityToken || 'CAMPAIGN_DEFAULT'
              }));
              patientCursor++;
            }
          }
          setProcessList(prev => {
            const next = [...prev];
            next[i] = { ...next[i], errorReason: t('reservations.massiveCharge.step4ProcessingCreating') };
            return next;
          });
          const firstPatientOfGroup = item.dataSourceSubset[0] || {};
          const groupCostCenter = firstPatientOfGroup.costCenter;
          const inferredCostCenterId = typeof groupCostCenter === 'object' && groupCostCenter !== null && groupCostCenter.id
            ? groupCostCenter.id 
            : (formValues.cost_center_id || null);
          const reservationData = {
            user_id: item.applicantId,
            company_id: formValues.company_id,
            child_company_id: formValues.child_company_id || null,
            third_company_id: formValues.third_company_id || null,
            cost_center_id: inferredCostCenterId,
            invoice_company_id: formValues.invoice_company_id || null,
            outsourcing_id: formValues.outsourcing_id || null,
            contact_method: 'way_web',
            branch_office_id: branchOfficeId,
            reservation_date: mDate,
            total_patients: finalPatients.length,
            patients: finalPatients
          };
          const createRes = await createReservation(reservationData, branchOfficeId);
          if (!createRes || !createRes.id) {
            throw new Error(t('reservations.massiveCharge.step4ErrorMissingId'));
          }
          setProcessList(prev => {
            const next = [...prev];
            next[i] = { ...next[i], reservationId: createRes.id, reservationNumber: createRes.number || createRes.id, errorReason: t('reservations.massiveCharge.step4ProcessingConfirming') };
            return next;
          });
          const confirmRes = await reservationToConfirm({ reservation_id: createRes.id, action: 'confirm' });
          if (!confirmRes) {
            throw new Error(t('reservations.massiveCharge.step4ErrorConfirm', { id: createRes.id }));
          }
          setProcessList(prev => {
            const next = [...prev];
            next[i] = { ...next[i], status: 'confirmed', errorReason: '' };
            return next;
          });
          item.dataSourceSubset.forEach(p => {
             p._assignedReservation = createRes.number || createRes.id;
          });
        } catch (error) {
          setProcessList(prev => {
            const next = [...prev];
            next[i] = { ...next[i], status: 'error', errorReason: error.message || t('reservations.massiveCharge.step4ErrorUnknown') };
            return next;
          });
        }
      }
      setIsProcessing(false);
    };
    runTasks();
  }, [finalDataSource, form, baseData, overCapacityToken, t]);
  const columns = [
    {
      title: t('reservations.massiveCharge.step4ColDate'),
      dataIndex: 'date',
      key: 'date',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: t('reservations.massiveCharge.step4ColApplicant'),
      dataIndex: 'applicantName',
      key: 'applicantName',
    },
    {
      title: t('reservations.massiveCharge.step4ColPatients'),
      dataIndex: 'totalPatients',
      key: 'totalPatients',
    },
    {
      title: t('reservations.massiveCharge.step4ColResNumber'),
      dataIndex: 'reservationNumber',
      key: 'reservationNumber',
      render: (num) => num ? <strong>{num}</strong> : '-'
    },
    {
      title: t('reservations.massiveCharge.step4ColStatus'),
      key: 'status',
      width: 150,
      render: (_, record) => {
        if (record.status === 'pending') {
          return <Tag icon={<ClockCircleOutlined />} color="default" className='cmt-tag'>{t('reservations.massiveCharge.step4TagPending')}</Tag>;
        }
        if (record.status === 'processing') {
          return <Tag icon={<SyncOutlined spin />} className="cmt-tag cmt-tag-processing">{record.errorReason || t('reservations.massiveCharge.step4TagProcessing')}</Tag>;
        }
        if (record.status === 'error') {
          return <Tag icon={<CloseCircleOutlined />} className="cmt-tag cmt-tag-error">{t('reservations.massiveCharge.step4TagError')}</Tag>;
        }
        if (record.status === 'confirmed') {
          return <Tag icon={<CheckCircleOutlined />} className="cmt-tag cmt-tag-success">{t('reservations.massiveCharge.step4TagConfirmed')}</Tag>;
        }
        return <Tag>{record.status}</Tag>;
      }
    },
    {
      title: t('reservations.massiveCharge.step4ColValidation'),
      key: 'errorReason',
      render: (_, record) => {
        if (record.status === 'error') {
          return <span className="mc-text-error">{record.errorReason}</span>;
        }
        if (record.status === 'confirmed') {
          return <span className="mc-text-success">{t('reservations.massiveCharge.step4ValidationSuccess')}</span>;
        }
        return <span className="mc-text-muted">-</span>;
      }
    }
  ];
  const totalConfirmed = processList.filter(p => p.status === 'confirmed').length;
  const totalErrors = processList.filter(p => p.status === 'error').length;
  const totalProcessed = processList.filter(p => ['confirmed', 'error'].includes(p.status)).length;
  return (
    <div className="massive-charge-step4">
      <Row justify="center" className="mb-20">
        <Col span={24}>
          <h3 className="massive-charge-step-title">{t('reservations.massiveCharge.step4Title')}</h3>
          <p className="massive-charge-step-description">
            {t('reservations.massiveCharge.step4Desc')}
          </p>
        </Col>
      </Row>
      <Row className="mb-20">
        <Col span={24}>
          <div className="massive-charge-general-summary">
            <h4 className="summary-title">{t('reservations.massiveCharge.step4GeneralStatus')}</h4>
            <div className="summary-content">
              <strong>{t('reservations.massiveCharge.step4ProcessedTitle')}</strong> {totalProcessed} / {processList.length} {t('reservations.massiveCharge.step4GroupsCount')} <br />
              <strong>{t('reservations.massiveCharge.step4ConfirmedCount')}</strong> <span className="mc-text-success">{totalConfirmed}</span> | <strong>{t('reservations.massiveCharge.step4ErrorsCount')}</strong> <span className="mc-text-error">{totalErrors}</span>
            </div>
          </div>
        </Col>
      </Row>
      <div className="massive-charge-table-container">
        <Table
          dataSource={processList}
          columns={columns}
          pagination={false}
          size="middle"
          bordered
        />
      </div>
      <Row justify="end" className="mt-30" gutter={16}>
        <Col>
          <Button 
            className="cmt-button secondary outline" 
            size="large"
            disabled={isProcessing}
            onClick={async () => {
              try {
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet(t('reservations.massiveCharge.step4SheetTitle'));
                const dynamicColumns = (fieldsToInclude || []).map(f => ({
                  header: t(`reservations.massiveCharge.fields.${f.key}`, f.key),
                  key: f.key,
                  width: 20
                }));
                worksheet.columns = [
                  ...dynamicColumns,
                  { header: t('reservations.massiveCharge.step4ColResNumber'), key: '_assignedReservation', width: 20 },
                  { header: t('reservations.massiveCharge.step4BlockHeader'), key: '_assignedBlock', width: 30 }
                ];
                worksheet.getRow(1).font = { bold: true };
                finalDataSource.forEach(p => {
                  const rowData = {};
                  (fieldsToInclude || []).forEach(field => {
                    const val = p[field.key];
                    if (field.key === 'protocol') {
                      rowData[field.key] = (val || []).map(pr => pr.name || pr._originalName).join(', ');
                    } else if (field.key === 'applicant') {
                      rowData[field.key] = typeof val === 'object' && val ? (val.email || val.fullname || val.name) : (val || '');
                    } else if (['costCenter', 'project'].includes(field.key)) {
                      rowData[field.key] = typeof val === 'object' && val ? (val.name || val.business_name) : (val || '');
                    } else if (['area', 'jobPosition'].includes(field.key)) {
                      rowData[field.key] = typeof val === 'object' && val ? val.name : (val || '');
                    } else {
                      rowData[field.key] = val !== undefined && val !== null ? val : '';
                    }
                  });
                  rowData['_assignedReservation'] = p._assignedReservation || t('reservations.massiveCharge.step4MissingInfo');
                  rowData['_assignedBlock'] = p._assignedBlock || t('reservations.massiveCharge.step4MissingInfo');
                  worksheet.addRow(rowData);
                });
                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Resumen_Carga_Masiva_${moment().format('YYYYMMDD_HHmm')}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch (err) {
                console.error('Error exportando Excel:', err);
              }
            }}
          >
            {t('reservations.massiveCharge.step4DownloadSummary')}
          </Button>
        </Col>
        <Col>
          <Button 
            type="primary" 
            className="cmt-button primary" 
            size="large"
            onClick={handleCancel}
            disabled={isProcessing}
          >
            {t('reservations.massiveCharge.step4CloseAndSee')}
          </Button>
        </Col>
      </Row>
    </div>
  );
};
export default MassiveChargeStep4;
